import {useCallback, useEffect, useImperativeHandle, useRef, useState, type Ref} from 'react'
import {gsap} from 'gsap'
import {MathUtils, Vector3} from 'three'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import {MonaCanvas, type MonaCanvasBounds} from '../MonaCanvas'
import styles from './MonaHead.module.css'
import blackFallback from './assets/fallback-monahead-black.webp'
import purpleFallback from './assets/fallback-monahead-purple.webp'

const fallbackImages = {
  black: blackFallback,
  purple: purpleFallback,
}

export interface MonaHeadHandle {
  enterMona: () => void
}

export interface MonaHeadProps {
  variant: 'black' | 'purple'
  alt?: string
  isAutoplay?: boolean
  ref?: Ref<MonaHeadHandle>
}

function MonaHeadArtwork({
  variant,
  onFallback,
  isAutoplay,
  entranceRequest,
}: Pick<MonaHeadProps, 'variant'> & {
  onFallback: () => void
  isAutoplay: boolean
  entranceRequest: number
}) {
  const restingPitchRadians = MathUtils.degToRad(-8)
  const artworkRef = useRef<HTMLDivElement>(null)
  const openingFaceRef = useRef<Vector3 | null>(new Vector3(-1, 0, 1))
  const advanceOpeningRef = useRef<((timestamp: number) => void) | null>(null)
  const [isReady, setIsReady] = useState(false)
  const hasStartedRef = useRef(false)
  const handleBeforeRender = useCallback((timestamp: number) => advanceOpeningRef.current?.(timestamp), [])
  const handleBoundsChange = useCallback((bounds: MonaCanvasBounds | null) => {
    if (!bounds || hasStartedRef.current) return

    hasStartedRef.current = true
    setIsReady(true)
  }, [])

  useEffect(() => {
    const artwork = artworkRef.current
    if (!isReady || !artwork || (!isAutoplay && entranceRequest === 0)) return

    const openingFace = openingFaceRef.current ?? new Vector3(-1, 0, 1)
    openingFaceRef.current = openingFace

    const context = gsap.context(() => {
      const gaze = {angle: Math.PI * 0.8, radius: 1, restingPitch: 0}
      const updateGaze = () => {
        const forwardDepth = 2
        openingFace.set(
          gaze.radius * Math.cos(gaze.angle),
          gaze.radius * Math.sin(gaze.angle) - Math.tan(gaze.restingPitch) * forwardDepth,
          forwardDepth,
        )
      }
      updateGaze()
      const timeline = gsap.timeline({
        paused: true,
        onUpdate: updateGaze,
        onComplete: () => {
          openingFaceRef.current = null
          advanceOpeningRef.current = null
        },
      })

      // Avoid switching transform modes when the entrance ends but the WebGL gaze is still moving.
      timeline.set(artwork, {yPercent: 60, opacity: 1, scaleX: 1, scaleY: 1, force3D: true})
      timeline.to(artwork, {yPercent: 0, duration: 1.5, ease: 'elastic.out(1,0.5)'}, 0)

      timeline.addLabel('lookAround', 0.0)
      timeline.to(gaze, {angle: Math.PI * 0.2, duration: 2, ease: 'power2.out'}, 'lookAround')
      timeline.addLabel('faceForward', 0.7)
      timeline.to(
        gaze,
        {radius: 0, restingPitch: restingPitchRadians, duration: 1, ease: 'power2.inOut'},
        'faceForward',
      )

      let startTime: number | undefined
      advanceOpeningRef.current = timestamp => {
        if (startTime === undefined) startTime = timestamp
        timeline.totalTime((timestamp - startTime) / 1000)
      }
    }, artwork)

    return () => {
      advanceOpeningRef.current = null
      context.revert()
    }
  }, [isReady, restingPitchRadians, isAutoplay, entranceRequest])

  return (
    <div className={styles.Artwork} data-ready={isReady} ref={artworkRef}>
      <MonaCanvas
        colorVariant={variant}
        framing="contain"
        mouseInteractionType="face"
        mouseInteractionRadius={600}
        mouseInteractionRestingPitch={restingPitchRadians}
        faceDirectionRef={openingFaceRef}
        onBeforeRender={handleBeforeRender}
        onFallback={onFallback}
        onMonaCanvasBoundsChange={handleBoundsChange}
      />
    </div>
  )
}

export function MonaHead({variant, alt, isAutoplay = true, ref}: MonaHeadProps) {
  const prefersReducedMotion = usePrefersReducedMotion()
  const [showFallback, setShowFallback] = useState(false)
  const [entranceRequest, setEntranceRequest] = useState(0)
  const handleFallback = useCallback(() => setShowFallback(true), [])

  useImperativeHandle(
    ref,
    () => ({
      enterMona: () => setEntranceRequest(request => request + 1),
    }),
    [],
  )

  return (
    <div
      aria-hidden={alt ? undefined : true}
      aria-label={alt || undefined}
      className={styles.MonaHead}
      role={alt ? 'img' : undefined}
    >
      {prefersReducedMotion || showFallback ? (
        <img
          alt=""
          aria-hidden="true"
          className={styles.Fallback}
          src={fallbackImages[variant]}
          width={200}
          height={200}
        />
      ) : (
        <MonaHeadArtwork
          key={variant}
          variant={variant}
          onFallback={handleFallback}
          isAutoplay={isAutoplay}
          entranceRequest={entranceRequest}
        />
      )}
    </div>
  )
}
